// Carteira de projetos (PIs): quanto está vindo, o que não fatura e há quanto tempo.
//
// A tela lê um "retrato" (importação da planilha de carteira). Os filtros do topo
// valem para tudo abaixo — KPIs, gráficos e tabela saem do mesmo recorte, para o
// número do cartão bater com o da linha.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AlertTriangle, CalendarClock, Loader2, PauseCircle,
  RefreshCw, Search, TrendingUp, Upload, Users, Wallet,
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useTheme } from '@/contexts/theme-context';
import { useAuth } from '@/contexts/auth-hook';
import {
  CaixaTooltip, CartaoGrafico, ChipStatus, Legenda, StatTile, TabelaEquivalente,
} from './carteira-ui';
import { obterCoresValor, obterRampaAging, obterVizTheme } from './carteira-viz';
import {
  ImportacaoCarteira, PiLinha, ResumoCarteira, STATUS_META, STATUS_ORDEM,
  dataCurta, mesPorExtenso, mesPorExtensoCapitalizado, moeda, moedaCurta, moedaExata, percentual,
} from './carteira-types';
import { CustoAcumulado } from './custo-types';
import { ImportarCarteiraModal } from './ImportarCarteiraModal';
import { PiDetalheModal } from './PiDetalheModal';

/** Teto de linhas da tabela — a base inteira passa de mil PIs. */
const LIMITE_TABELA = 300;

interface Filtros {
  importacaoId: string;
  squad: string;
  situacao: string;
  famComl: string;
  regiao: string;
  busca: string;
  apenasComSaldo: boolean;
  status: string;
  ordenar: string;
}

const FILTROS_INICIAIS: Filtros = {
  importacaoId: '',
  squad: '',
  situacao: '',
  famComl: '',
  regiao: '',
  busca: '',
  apenasComSaldo: false,
  status: '',
  ordenar: 'pendente',
};

const classeSelect =
  'px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg ' +
  'bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white text-sm';

const classeRotulo =
  'block text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light mb-1';

export function CarteiraPage() {
  const { theme } = useTheme();
  const { isAdmin } = useAuth();
  const viz = obterVizTheme(theme);
  const cores = obterCoresValor(theme);
  const rampaAging = obterRampaAging(theme);

  const [filtros, setFiltros] = useState<Filtros>(FILTROS_INICIAIS);
  /** Texto do campo de busca, aplicado com atraso para não bater na API a cada tecla. */
  const [buscaDigitada, setBuscaDigitada] = useState('');

  const [resumo, setResumo] = useState<ResumoCarteira | null>(null);
  const [pis, setPis] = useState<PiLinha[]>([]);
  const [totalPis, setTotalPis] = useState(0);
  const [importacoes, setImportacoes] = useState<ImportacaoCarteira[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [mostrarImportar, setMostrarImportar] = useState(false);
  const [piSelecionado, setPiSelecionado] = useState<string | null>(null);
  /** Custo por PI só existe para admin — o rateio parte do custo por pessoa. */
  const [custo, setCusto] = useState<CustoAcumulado | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => ({ ...f, busca: buscaDigitada })), 400);
    return () => clearTimeout(t);
  }, [buscaDigitada]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const comuns = {
        importacaoId: filtros.importacaoId || undefined,
        squad: filtros.squad || undefined,
        situacao: filtros.situacao || undefined,
        famComl: filtros.famComl || undefined,
        regiao: filtros.regiao || undefined,
        busca: filtros.busca || undefined,
        apenasComSaldo: filtros.apenasComSaldo,
      };

      const [resResumo, resPis, resImp, resCusto] = await Promise.all([
        apiService.obterResumoCarteira(comuns),
        apiService.listarPisCarteira({
          ...comuns,
          status: filtros.status || undefined,
          ordenar: filtros.ordenar,
          limite: LIMITE_TABELA,
        }),
        apiService.listarImportacoesCarteira(),
        // Não-admin recebe 403 aqui; a tela simplesmente não mostra custo.
        isAdmin ? apiService.obterCustoAcumuladoCarteira() : Promise.resolve(null),
      ]);

      if (!resResumo.success) throw new Error(resResumo.message || 'Falha ao carregar o resumo');
      setResumo(resResumo.data as ResumoCarteira);
      setPis((resPis.data as { pis: PiLinha[] })?.pis ?? []);
      setTotalPis((resPis.data as { total: number })?.total ?? 0);
      setImportacoes((resImp.data as ImportacaoCarteira[]) ?? []);
      setCusto(resCusto?.success ? (resCusto.data as CustoAcumulado) : null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível carregar a carteira');
    } finally {
      setCarregando(false);
    }
  }, [filtros, isAdmin]);

  useEffect(() => { carregar(); }, [carregar]);

  const ajustar = (mudanca: Partial<Filtros>) => setFiltros((f) => ({ ...f, ...mudanca }));

  const limpar = () => {
    setBuscaDigitada('');
    setFiltros((f) => ({ ...FILTROS_INICIAIS, importacaoId: f.importacaoId }));
  };

  const filtrosAtivos =
    !!filtros.squad || !!filtros.situacao || !!filtros.famComl || !!filtros.regiao ||
    !!filtros.busca || filtros.apenasComSaldo || !!filtros.status;

  // ---------------------------------------------------------------- gráficos
  const serie = resumo?.serieMensal ?? [];
  const dadosSerie = useMemo(
    () => serie.map((p) => ({
      ...p,
      // Uma barra por período: só uma das duas chaves é preenchida, então o
      // empilhamento nunca soma dois valores — ele só escolhe a cor certa.
      realizadoValor: p.realizado ? p.valor : 0,
      previstoValor: p.realizado ? 0 : p.valor,
    })),
    [serie]
  );
  const temPrevisto = serie.some((p) => !p.realizado);

  const squads = useMemo(
    () => (resumo?.porSquad ?? []).filter((s) => s.saldo > 0).slice(0, 10),
    [resumo]
  );
  const aging = resumo?.porAging ?? [];
  const regioes = useMemo(
    () => (resumo?.porRegiao ?? []).filter((r) => r.aberto > 0).slice(0, 10),
    [resumo]
  );

  /** Custo acumulado por código de PI, para casar com as linhas da tabela. */
  const custoPorPi = useMemo(
    () => new Map((custo?.porPi ?? []).map((p) => [p.piCodigo, p])),
    [custo]
  );
  const mostrarCusto = isAdmin && !!custo && custo.porPi.length > 0;

  const kpis = resumo?.kpis;
  const importacaoAtual = resumo?.importacao;

  if (carregando && !resumo) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" aria-hidden />
        <span className="sr-only">Carregando carteira</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------------- cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">
            Carteira de Projetos
          </h1>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            {importacaoAtual
              ? <>Retrato de <strong className="text-schmersal-gray-dark dark:text-white">{mesPorExtenso(importacaoAtual.referencia)}</strong> — {importacaoAtual.arquivo}</>
              : 'Nenhuma planilha importada ainda'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={carregar}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} aria-hidden />
            Atualizar
          </button>
          <button
            onClick={() => setMostrarImportar(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
          >
            <Upload className="h-4 w-4" aria-hidden />
            Importar planilha
          </button>
        </div>
      </div>

      {erro && (
        <div className="flex items-start gap-3 rounded-lg border border-schmersal-yellow-dark/40 bg-schmersal-yellow/10 p-4">
          <AlertTriangle className="h-5 w-5 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">{erro}</p>
        </div>
      )}

      {!importacaoAtual ? (
        <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-10 border border-rg-line dark:border-schmersal-gray-dark text-center">
          <Wallet className="h-10 w-10 mx-auto text-schmersal-gray-medium" aria-hidden />
          <h2 className="mt-3 font-semibold text-schmersal-gray-dark dark:text-white">
            Nenhuma carteira importada
          </h2>
          <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            Envie a planilha de carteira (abas “PI’s + Saldo” e “Carteira …”) para montar o painel.
          </p>
          <button
            onClick={() => setMostrarImportar(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
          >
            <Upload className="h-4 w-4" aria-hidden />
            Importar planilha
          </button>
        </div>
      ) : (
        <>
          {/* --------------------------------------------------------- filtros */}
          <div className="flex flex-wrap items-end gap-3 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4 border border-rg-line dark:border-schmersal-gray-dark">
            {importacoes.length > 1 && (
              <div>
                <label htmlFor="f-retrato" className={classeRotulo}>Retrato</label>
                <select
                  id="f-retrato"
                  className={classeSelect}
                  value={filtros.importacaoId}
                  onChange={(e) => ajustar({ importacaoId: e.target.value })}
                >
                  <option value="">Mais recente</option>
                  {importacoes.map((i) => (
                    <option key={i.id} value={i.id}>
                      {mesPorExtenso(i.referencia)} — {i.arquivo}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label htmlFor="f-squad" className={classeRotulo}>Squad</label>
              <select
                id="f-squad"
                className={classeSelect}
                value={filtros.squad}
                onChange={(e) => ajustar({ squad: e.target.value })}
              >
                <option value="">Todos</option>
                {resumo?.opcoes.squads.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="f-situacao" className={classeRotulo}>Situação</label>
              <select
                id="f-situacao"
                className={classeSelect}
                value={filtros.situacao}
                onChange={(e) => ajustar({ situacao: e.target.value })}
              >
                <option value="">Todas</option>
                {resumo?.opcoes.situacoes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="f-status" className={classeRotulo}>Status</label>
              <select
                id="f-status"
                className={classeSelect}
                value={filtros.status}
                onChange={(e) => ajustar({ status: e.target.value })}
              >
                <option value="">Todos</option>
                {STATUS_ORDEM.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].rotulo}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="f-regiao" className={classeRotulo}>Região (pedidos)</label>
              <select
                id="f-regiao"
                className={classeSelect}
                value={filtros.regiao}
                onChange={(e) => ajustar({ regiao: e.target.value })}
              >
                <option value="">Todas</option>
                {resumo?.opcoes.regioes.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label htmlFor="f-busca" className={classeRotulo}>Buscar PI</label>
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium"
                  aria-hidden
                />
                <input
                  id="f-busca"
                  type="search"
                  value={buscaDigitada}
                  onChange={(e) => setBuscaDigitada(e.target.value)}
                  placeholder="código, cliente ou observação"
                  className={`${classeSelect} w-full pl-9`}
                />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-schmersal-gray-dark dark:text-white pb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filtros.apenasComSaldo}
                onChange={(e) => ajustar({ apenasComSaldo: e.target.checked })}
                className="rounded border-schmersal-gray-light"
              />
              Só com saldo a faturar
            </label>

            {filtrosAtivos && (
              <button
                onClick={limpar}
                className="pb-2 text-sm text-schmersal-blue dark:text-schmersal-turquoise hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </div>

          {/* ------------------------------------------------------------ KPIs */}
          {kpis && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <StatTile
                rotulo="Carteira total"
                valor={moedaCurta(kpis.saldoTotal)}
                detalhe={`${kpis.comSaldo.toLocaleString('pt-BR')} PIs com saldo, de ${kpis.totalPis.toLocaleString('pt-BR')} no recorte`}
                icone={<Wallet className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />}
              />
              <StatTile
                rotulo="Faturado"
                valor={moedaCurta(kpis.faturadoTotal)}
                detalhe={`${percentual(kpis.percentualFaturado)} da carteira · só meses fechados`}
                icone={<TrendingUp className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />}
              />
              <StatTile
                rotulo="Ainda a faturar"
                valor={moedaCurta(kpis.pendenteTotal)}
                detalhe={`${kpis.ativos} PIs ativos · ${moedaCurta(kpis.saldoAtivos)} em carteira`}
              />
              <StatTile
                rotulo="Parado"
                valor={moedaCurta(kpis.saldoParado)}
                tom="alerta"
                detalhe={`${kpis.parados} PIs sem faturar há 3 meses ou mais`}
                icone={<PauseCircle className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />}
              />
              <StatTile
                rotulo="Nunca faturou"
                valor={moedaCurta(kpis.saldoSemFaturar)}
                tom="alerta"
                detalhe={`${kpis.semFaturar} PIs ativos sem nenhum lançamento`}
              />
              {/* Não repetir a carteira aqui: o saldo do PI já é a soma dos
                  pedidos em aberto dele. O que falta é o horizonte — quanto a
                  planilha ainda promete nos meses que não fecharam. */}
              <StatTile
                rotulo="Previsto a entrar"
                valor={moedaCurta(kpis.previstoNoPeriodo)}
                detalhe={kpis.ultimoPeriodoPrevisto
                  ? `Programado até ${kpis.ultimoPeriodoPrevisto}, com o mês em curso`
                  : 'Nada programado a partir do mês em curso'}
                icone={<CalendarClock className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />}
              />
            </div>
          )}

          {mostrarCusto && custo && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatTile
                rotulo="Custo acumulado do time"
                valor={moedaCurta(custo.totais.custoTotal)}
                detalhe={`${custo.porPi.length} PIs com gente alocada, em ${custo.totais.meses} ${custo.totais.meses === 1 ? 'mês' : 'meses'}`}
                icone={<Users className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />}
              />
              {/* Margem só dos PIs que TÊM custo lançado. Comparar o faturado da
                  carteira inteira com o custo de uns poucos daria um número falso. */}
              <StatTile
                rotulo="Margem dos PIs com equipe"
                valor={moedaCurta(custo.totais.margem)}
                detalhe={`${moedaCurta(custo.totais.faturadoDosPis)} faturado nesses PIs, menos o custo${
                  custo.totais.pisForaDoRetrato ? ` · ${custo.totais.pisForaDoRetrato} fora do retrato atual` : ''
                }`}
              />
              <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4 border border-rg-line dark:border-schmersal-gray-dark flex items-center">
                <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  O custo vem da alocação do time.{' '}
                  <Link to="/carteira/equipe" className="text-schmersal-blue dark:text-schmersal-turquoise hover:underline">
                    Lançar alocação e custo
                  </Link>
                  {custo.totais.pessoasSemCusto.length > 0 && (
                    <span className="block mt-1 text-schmersal-yellow-dark">
                      {custo.totais.pessoasSemCusto.length} pessoa(s) alocadas ainda sem custo — o total está por baixo.
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* --------------------------------------------------------- alertas */}
          {resumo && (resumo.alertas.semPrevisao > 0 || resumo.alertas.previsaoVencida > 0 || resumo.alertas.custoAcimaDoSaldo > 0) && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker px-4 py-3">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-schmersal-gray-dark dark:text-white">
                <CalendarClock className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />
                Pontos de atenção nos PIs ativos
              </span>
              <span className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
                <strong className="tabular-nums">{resumo.alertas.semPrevisao}</strong> sem previsão de encerramento
              </span>
              <span className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
                <strong className="tabular-nums">{resumo.alertas.previsaoVencida}</strong> com previsão já vencida
              </span>
              <span className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
                <strong className="tabular-nums">{resumo.alertas.custoAcimaDoSaldo}</strong> com custo para finalizar acima do saldo
              </span>
            </div>
          )}

          {/* -------------------------------------------------------- gráficos */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <CartaoGrafico
                titulo="Quanto entra por mês"
                subtitulo={`Só mês fechado conta como faturado. ${mesPorExtensoCapitalizado(importacaoAtual.referencia)} ainda está em curso, então entra como previsão junto com os meses seguintes.`}
              >
                {temPrevisto && (
                  <Legenda itens={[
                    { nome: 'Faturado (mês fechado)', cor: cores.realizado },
                    { nome: 'Previsto (inclui o mês em curso)', cor: cores.previsto },
                  ]} />
                )}
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={dadosSerie} margin={{ top: 30, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid stroke={viz.grade} strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="rotulo"
                      tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                      axisLine={{ stroke: viz.eixo }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v: number) => moedaCurta(v)}
                      tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={78}
                    />
                    <Tooltip
                      cursor={{ fill: viz.grade, fillOpacity: 0.35 }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload as (typeof dadosSerie)[number];
                        return (
                          <CaixaTooltip
                            titulo={p.rotulo}
                            linhas={[
                              {
                                nome: p.realizado ? 'faturado' : p.emCurso ? 'em curso, fecha no fim do mês' : 'previsto',
                                valor: moedaExata(p.valor),
                                cor: p.realizado ? cores.realizado : cores.previsto,
                              },
                              {
                                nome: p.realizado ? 'acumulado no ano' : 'acumulado se a previsão se confirmar',
                                valor: moedaExata(p.acumulado),
                              },
                              { nome: p.pis === 1 ? 'PI' : 'PIs', valor: String(p.pis) },
                            ]}
                          />
                        );
                      }}
                    />
                    {/* stackId comum + uma chave zerada por barra: o empilhamento
                        aqui só serve para dar duas cores e uma legenda honesta. */}
                    <Bar dataKey="realizadoValor" stackId="fat" fill={cores.realizado} radius={[4, 4, 0, 0]} maxBarSize={44}>
                      <LabelList
                        dataKey="realizadoValor"
                        position="top"
                        formatter={(v: unknown) => (Number(v) ? moedaCurta(Number(v)) : '')}
                        style={{ fill: viz.textoSecundario, fontSize: 11 }}
                      />
                    </Bar>
                    <Bar dataKey="previstoValor" stackId="fat" fill={cores.previsto} radius={[4, 4, 0, 0]} maxBarSize={44}>
                      <LabelList
                        dataKey="previstoValor"
                        position="top"
                        formatter={(v: unknown) => (Number(v) ? moedaCurta(Number(v)) : '')}
                        style={{ fill: viz.textoSecundario, fontSize: 11 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <TabelaEquivalente
                  colunas={['Período', 'Valor', 'Acumulado', 'PIs']}
                  linhas={serie.map((p) => [
                    `${p.rotulo}${p.realizado ? '' : p.emCurso ? ' (em curso)' : ' (previsto)'}`,
                    p.valor,
                    p.acumulado,
                    String(p.pis),
                  ])}
                />
              </CartaoGrafico>
            </div>

            <CartaoGrafico
              titulo="Há quanto tempo não fatura"
              subtitulo="Saldo a faturar dos PIs ativos, por tempo desde o último faturamento."
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={aging}
                  layout="vertical"
                  margin={{ top: 4, right: 68, left: 8, bottom: 4 }}
                >
                  <CartesianGrid stroke={viz.grade} strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="curto"
                    tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={54}
                  />
                  <Tooltip
                    cursor={{ fill: viz.grade, fillOpacity: 0.35 }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof aging)[number];
                      return (
                        <CaixaTooltip
                          titulo={p.rotulo}
                          linhas={[
                            { nome: 'a faturar', valor: moedaExata(p.pendente) },
                            { nome: p.quantidade === 1 ? 'PI' : 'PIs', valor: String(p.quantidade) },
                          ]}
                        />
                      );
                    }}
                  />
                  <Bar dataKey="pendente" radius={[0, 4, 4, 0]} maxBarSize={30}>
                    {aging.map((a, i) => (
                      <Cell key={a.faixa} fill={rampaAging[i] ?? rampaAging[rampaAging.length - 1]} />
                    ))}
                    <LabelList
                      dataKey="pendente"
                      position="right"
                      formatter={(v: unknown) => (Number(v) ? moedaCurta(Number(v)) : '')}
                      style={{ fill: viz.textoSecundario, fontSize: 11 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <TabelaEquivalente
                colunas={['Faixa', 'A faturar', 'PIs']}
                linhas={aging.map((a) => [a.rotulo, a.pendente, String(a.quantidade)])}
              />
            </CartaoGrafico>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <CartaoGrafico
                titulo="Onde está a carteira"
                subtitulo="Por squad: quanto da carteira já virou faturamento em mês fechado e quanto ainda está por vir."
              >
                <Legenda itens={[
                  { nome: 'Faturado (mês fechado)', cor: cores.realizado },
                  { nome: 'A faturar', cor: cores.previsto },
                ]} />
                <ResponsiveContainer width="100%" height={Math.max(220, squads.length * 34 + 40)}>
                  <BarChart
                    data={squads}
                    layout="vertical"
                    margin={{ top: 4, right: 76, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid stroke={viz.grade} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v: number) => moedaCurta(v)}
                      tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                      axisLine={{ stroke: viz.eixo }} tickLine={false} />
                    <YAxis
                      type="category"
                      dataKey="squad"
                      tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={132}
                    />
                    <Tooltip
                      cursor={{ fill: viz.grade, fillOpacity: 0.35 }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload as (typeof squads)[number];
                        return (
                          <CaixaTooltip
                            titulo={p.squad}
                            linhas={[
                              { nome: 'faturado', valor: moedaExata(p.faturado), cor: cores.realizado },
                              { nome: 'a faturar', valor: moedaExata(p.pendente), cor: cores.previsto },
                              { nome: 'carteira', valor: moedaExata(p.saldo) },
                              { nome: p.quantidade === 1 ? 'PI' : 'PIs', valor: String(p.quantidade) },
                            ]}
                          />
                        );
                      }}
                    />
                    {/* 2px de superfície entre os segmentos: o stroke da cor do
                        cartão separa faturado de pendente sem inventar um gap. */}
                    <Bar dataKey="faturado" stackId="c" fill={cores.realizado} maxBarSize={26}
                      stroke={viz.superficie} strokeWidth={2} />
                    <Bar dataKey="pendente" stackId="c" fill={cores.previsto} maxBarSize={26}
                      stroke={viz.superficie} strokeWidth={2} radius={[0, 4, 4, 0]}>
                      <LabelList
                        dataKey="saldo"
                        position="right"
                        formatter={(v: unknown) => moedaCurta(Number(v))}
                        style={{ fill: viz.textoSecundario, fontSize: 11 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <TabelaEquivalente
                  colunas={['Squad', 'Faturado', 'A faturar', 'Carteira', 'PIs']}
                  linhas={squads.map((s) => [s.squad, s.faturado, s.pendente, s.saldo, String(s.quantidade)])}
                />
              </CartaoGrafico>
            </div>

            <CartaoGrafico
              titulo="A carteira por região"
              subtitulo="Os mesmos pedidos que formam o saldo, vistos pela região que os atende."
            >
              <ResponsiveContainer width="100%" height={Math.max(220, regioes.length * 30 + 40)}>
                <BarChart
                  data={regioes}
                  layout="vertical"
                  margin={{ top: 4, right: 72, left: 8, bottom: 4 }}
                >
                  <CartesianGrid stroke={viz.grade} strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="regiao"
                    tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                  />
                  <Tooltip
                    cursor={{ fill: viz.grade, fillOpacity: 0.35 }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof regioes)[number];
                      return (
                        <CaixaTooltip
                          titulo={`Região ${p.regiao}`}
                          linhas={[
                            { nome: 'em aberto', valor: moedaExata(p.aberto) },
                            { nome: 'pedido total', valor: moedaExata(p.pedido) },
                            { nome: p.quantidade === 1 ? 'item' : 'itens', valor: String(p.quantidade) },
                          ]}
                        />
                      );
                    }}
                  />
                  <Bar dataKey="aberto" fill={cores.realizado} radius={[0, 4, 4, 0]} maxBarSize={24}>
                    <LabelList
                      dataKey="aberto"
                      position="right"
                      formatter={(v: unknown) => moedaCurta(Number(v))}
                      style={{ fill: viz.textoSecundario, fontSize: 11 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {resumo && resumo.porTipo.length > 0 && (
                <div className="mt-4 pt-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
                  <p className="text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide mb-2">
                    Por tipo de carteira
                  </p>
                  <ul className="space-y-1">
                    {resumo.porTipo.map((t) => (
                      <li key={t.tipo} className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="text-schmersal-gray-dark dark:text-white">{t.tipo}</span>
                        <span className="tabular-nums font-medium text-schmersal-gray-dark dark:text-white">
                          {moeda(t.aberto)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CartaoGrafico>
          </div>

          {/* --------------------------------------------------------- tabela */}
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
              <div>
                <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">PIs do recorte</h2>
                <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">
                  {pis.length < totalPis
                    ? <>Mostrando os {pis.length} primeiros de <strong>{totalPis.toLocaleString('pt-BR')}</strong> — refine os filtros para ver o resto.</>
                    : <><strong>{totalPis.toLocaleString('pt-BR')}</strong> {totalPis === 1 ? 'PI' : 'PIs'}</>}
                </p>
              </div>
              <div>
                <label htmlFor="f-ordenar" className={classeRotulo}>Ordenar por</label>
                <select
                  id="f-ordenar"
                  className={classeSelect}
                  value={filtros.ordenar}
                  onChange={(e) => ajustar({ ordenar: e.target.value })}
                >
                  <option value="pendente">Maior saldo a faturar</option>
                  <option value="saldo">Maior carteira</option>
                  <option value="faturado">Maior faturamento</option>
                  <option value="parado">Mais tempo sem faturar</option>
                  <option value="codigo">Código do PI</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-schmersal-gray-light/40 dark:bg-schmersal-gray-dark/40">
                  <tr className="text-left">
                    <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">PI</th>
                    <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Squad</th>
                    <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Status</th>
                    <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Carteira</th>
                    <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right" title="Soma dos meses já fechados">Faturado</th>
                    <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right" title="Carteira menos o que já foi faturado — inclui o que está programado">A faturar</th>
                    <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Sem faturar</th>
                    {mostrarCusto && (
                      <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right" title="Custo do time acumulado neste PI">Custo</th>
                    )}
                    <th className="px-4 py-2.5 w-[170px] font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Previsão</th>
                  </tr>
                </thead>
                <tbody>
                  {pis.length === 0 && (
                    <tr>
                      <td colSpan={mostrarCusto ? 9 : 8} className="px-4 py-10 text-center text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        Nenhum PI bate com esses filtros.
                      </td>
                    </tr>
                  )}
                  {pis.map((pi) => (
                    <tr
                      key={pi.id}
                      onClick={() => setPiSelecionado(pi.id)}
                      className="border-t border-schmersal-gray-light dark:border-schmersal-gray-dark hover:bg-schmersal-gray-light/40 dark:hover:bg-schmersal-gray-dark/40 cursor-pointer"
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-schmersal-gray-dark dark:text-white">{pi.codigo}</span>
                        {pi.descricao && (
                          <span className="block text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light truncate max-w-[280px]">
                            {pi.descricao}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-schmersal-gray-dark dark:text-schmersal-gray-light">
                        {pi.squad ?? '—'}
                      </td>
                      <td className="px-4 py-2.5"><ChipStatus status={pi.status} compacto /></td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-schmersal-gray-dark dark:text-white">{moeda(pi.saldo)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-schmersal-gray-dark dark:text-white">{moeda(pi.faturado)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap font-medium text-schmersal-gray-dark dark:text-white">{moeda(pi.pendente)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-schmersal-gray-dark dark:text-schmersal-gray-light">
                        {pi.mesesSemFaturar === null
                          ? 'nunca faturou'
                          : `${pi.mesesSemFaturar} ${pi.mesesSemFaturar === 1 ? 'mês' : 'meses'}`}
                      </td>
                      {mostrarCusto && (
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-schmersal-gray-dark dark:text-schmersal-gray-light">
                          {custoPorPi.has(pi.codigo) ? moeda(custoPorPi.get(pi.codigo)!.custoAcumulado) : '—'}
                        </td>
                      )}
                      {/* A previsão nem sempre é data — vem texto livre da planilha
                          ("Entre o segundo semestre de 2026 a…"), que sem teto de
                          largura empurra a tabela para fora da tela. */}
                      <td
                        className="px-4 py-2.5 w-[170px] max-w-[170px] text-schmersal-gray-dark dark:text-schmersal-gray-light"
                        title={pi.previsao ? undefined : (pi.previsaoTexto ?? undefined)}
                      >
                        <span className="block truncate">
                          {pi.previsao ? dataCurta(pi.previsao) : (pi.previsaoTexto ?? '—')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legenda dos status: a cor do chip nunca é a única pista, mas o
              significado de cada rótulo precisa estar escrito em algum lugar. */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
            {STATUS_ORDEM.map((s) => (
              <div key={s} className="flex items-start gap-2">
                <ChipStatus status={s} compacto />
                <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  {STATUS_META[s].descricao}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {mostrarImportar && (
        <ImportarCarteiraModal
          importacoes={importacoes}
          podeRemover={isAdmin}
          onFechar={() => setMostrarImportar(false)}
          onConcluido={() => { setMostrarImportar(false); setFiltros((f) => ({ ...f, importacaoId: '' })); carregar(); }}
        />
      )}

      {piSelecionado && (
        <PiDetalheModal
          id={piSelecionado}
          custo={mostrarCusto
            ? custoPorPi.get(pis.find((p) => p.id === piSelecionado)?.codigo ?? '') ?? null
            : null}
          onFechar={() => setPiSelecionado(null)}
        />
      )}
    </div>
  );
}
